/*
 * <delivery-estimate>
 *
 * Rechnet aus Fertigungs- und Versanddauer in Werktagen ein konkretes
 * Lieferdatum aus. Wochenenden, deutsche Feiertage und Betriebsferien
 * werden übersprungen.
 *
 * Gerechnet wird in ZWEI Phasen mit getrennten Kalendern, weil Fertigung
 * und Zustellung nicht dieselben Tage kennen:
 *
 *   Bestellung → [Fertigungskalender] → Paket geht raus
 *              → [Versandkalender]    → Lieferdatum
 *
 * Betriebsferien halten nur die Fertigung an – ein Paket, das schon bei
 * DHL liegt, läuft auch im Urlaub weiter. Ob samstags gefertigt bzw.
 * zugestellt wird, ist je Phase eigen einstellbar.
 *
 * Zwei bewusste Einschränkungen:
 *
 * 1. Das angezeigte Datum entsteht immer aus der MAXIMALEN Dauer. Es ist
 *    damit nie optimistischer als die statische Angabe daneben – eine zu
 *    günstige Lieferzusage wäre nach UWG angreifbar.
 * 2. Schlägt irgendetwas fehl, bleibt die im Markup gerenderte statische
 *    Angabe stehen. Es wird nie ein halbfertiger Zustand gezeigt.
 */

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        this.headlineEl = this.querySelector('[data-delivery-headline]');
        this.countdownEl = this.querySelector('[data-delivery-countdown]');
        if (!this.headlineEl) return;

        try {
          const holidays = this.parseDates(this.dataset.holidays, true);
          const closures = this.parseDates(this.dataset.closures, false);

          // Betriebsferien blockieren nur die Fertigung.
          const closedForProduction = new Set([...holidays, ...closures]);

          this.production = {
            blocked: closedForProduction,
            saturday: this.dataset.productionSaturday === 'true',
          };
          this.shipping = {
            blocked: holidays,
            saturday: this.dataset.shippingSaturday === 'true',
          };

          this.render();
          this.startCountdown();
        } catch (error) {
          // Statische Angabe bleibt stehen.
          console.warn('delivery-estimate:', error);
        }
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
      }

      render() {
        const productionMax = parseInt(this.dataset.productionMax, 10);
        const shippingMax = parseInt(this.dataset.shippingMax, 10);
        if ([productionMax, shippingMax].some(Number.isNaN)) return;

        const template = this.dataset.headline;
        if (!template) return;

        const start = this.effectiveStart();
        const shipped = this.addWorkingDays(start, productionMax, this.production);
        const latest = this.addWorkingDays(shipped, shippingMax, this.shipping);

        this.headlineEl.innerHTML = template.replace('{date}', this.wrap(this.formatDate(latest)));
      }

      /*
       * Bestellungen ab dem Annahmeschluss zählen erst ab dem nächsten
       * Fertigungstag. Das ist gleichzeitig die Grundlage für den Countdown.
       */
      effectiveStart() {
        const cutoffHour = parseInt(this.dataset.cutoffHour, 10);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const afterCutoff = !Number.isNaN(cutoffHour) && now.getHours() >= cutoffHour;
        const workingToday = this.isWorkingDay(today, this.production);

        if (afterCutoff || !workingToday) {
          return this.addWorkingDays(today, 1, this.production);
        }
        return today;
      }

      startCountdown() {
        if (this.dataset.showCountdown !== 'true' || !this.countdownEl) return;

        const template = this.dataset.countdown;
        const cutoffHour = parseInt(this.dataset.cutoffHour, 10);
        if (!template || Number.isNaN(cutoffHour)) return;

        const tick = () => {
          const now = new Date();
          const cutoff = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            cutoffHour,
            0,
            0
          );

          // Countdown nur an Fertigungstagen und nur vor dem Annahmeschluss.
          if (now >= cutoff || !this.isWorkingDay(now, this.production)) {
            this.countdownEl.hidden = true;
            if (this.timer) clearInterval(this.timer);
            return;
          }

          const remaining = cutoff - now;
          const hours = Math.floor(remaining / 3600000);
          const minutes = Math.floor((remaining % 3600000) / 60000);
          const value = hours > 0 ? `${hours} Std. ${minutes} Min.` : `${minutes} Min.`;

          this.countdownEl.innerHTML = template.replace('{time}', this.wrapCountdown(value));
          this.countdownEl.hidden = false;
        };

        tick();
        this.timer = setInterval(tick, 30000);
      }

      /*
       * Liest eine kommagetrennte Liste aus Einzeltagen und Zeiträumen
       * (2026-10-05..2026-10-09) ein. Mit `withPublicHolidays` kommen die
       * bundesweiten Feiertage des laufenden und des kommenden Jahres dazu.
       *
       * Ein Zeitraum wird auf 400 Tage begrenzt: ein Vertipper im Jahr
       * ("2036-10-09") würde sonst eine Endlosschleife bauen.
       */
      parseDates(raw, withPublicHolidays) {
        const set = new Set();

        if (withPublicHolidays) {
          const currentYear = new Date().getFullYear();
          for (let year = currentYear; year <= currentYear + 1; year += 1) {
            this.germanPublicHolidays(year).forEach((key) => set.add(key));
          }
        }

        if (!raw) return set;

        raw
          .split(',')
          .map((entry) => entry.trim())
          .forEach((entry) => {
            const single = /^(\d{4}-\d{2}-\d{2})$/.exec(entry);
            if (single) {
              set.add(single[1]);
              return;
            }

            const range = /^(\d{4}-\d{2}-\d{2})\s*\.\.\s*(\d{4}-\d{2}-\d{2})$/.exec(entry);
            if (!range) return;

            const from = this.fromKey(range[1]);
            const until = this.fromKey(range[2]);
            if (!from || !until || until < from) return;

            for (let guard = 0; from <= until && guard < 400; guard += 1) {
              set.add(this.toKey(from));
              from.setDate(from.getDate() + 1);
            }
          });

        return set;
      }

      germanPublicHolidays(year) {
        const easter = this.easterSunday(year);
        const fromEaster = (days) => {
          const date = new Date(easter.getTime());
          date.setDate(date.getDate() + days);
          return this.toKey(date);
        };

        return [
          `${year}-01-01`, // Neujahr
          fromEaster(-2), // Karfreitag
          fromEaster(1), // Ostermontag
          `${year}-05-01`, // Tag der Arbeit
          fromEaster(39), // Christi Himmelfahrt
          fromEaster(50), // Pfingstmontag
          `${year}-10-03`, // Tag der Deutschen Einheit
          `${year}-12-25`, // 1. Weihnachtsfeiertag
          `${year}-12-26`, // 2. Weihnachtsfeiertag
        ];
      }

      /* Gaußsche Osterformel. */
      easterSunday(year) {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return new Date(year, month - 1, day);
      }

      /*
       * Zählt `days` Arbeitstage des übergebenen Kalenders weiter. Bei 0
       * bleibt das Datum stehen, rückt aber auf den nächsten gültigen Tag
       * vor – sonst würde die zweite Phase auf einem Sonntag aufsetzen.
       */
      addWorkingDays(from, days, calendar) {
        const date = new Date(from.getTime());
        let remaining = days;

        while (remaining > 0) {
          date.setDate(date.getDate() + 1);
          if (this.isWorkingDay(date, calendar)) remaining -= 1;
        }

        while (!this.isWorkingDay(date, calendar)) {
          date.setDate(date.getDate() + 1);
        }

        return date;
      }

      isWorkingDay(date, calendar) {
        const weekday = date.getDay();
        if (weekday === 0) return false;
        if (weekday === 6 && !calendar.saturday) return false;
        return !calendar.blocked.has(this.toKey(date));
      }

      toKey(date) {
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      fromKey(key) {
        const [year, month, day] = key.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
      }

      formatDate(date) {
        return new Intl.DateTimeFormat('de-DE', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        }).format(date);
      }

      wrap(value) {
        return this.spanFor('delivery-estimate__date', value);
      }

      wrapCountdown(value) {
        return this.spanFor('delivery-estimate__countdown-value', value);
      }

      spanFor(className, value) {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = value;
        return span.outerHTML;
      }
    }
  );
}
